import PropTypes from 'prop-types';
import React from 'react';
import {CircularProgress} from '@material-ui/core';
import _ from 'underscore';

import {isNextPickup, rideStatus} from 'common/types/Ride';
import {Overlay} from 'common/components/BetterMap';
import Confirm from 'common/components/Confirm';
import DriverModal from 'ondemand/components/DriverModal';
import FloatingBarLayout from 'ondemand/components/FloatingBarLayout';
import OndemandMap from 'ondemand/components/OndemandMap';
import RideCancelModal from 'ondemand/components/RideCancelModal';
import RideDetailsInput from 'ondemand/components/RideDetailsInput';
import RideDirectionsMapLayer from 'ondemand/components/RideDirectionsMapLayer';
import RideRequestButton from 'ondemand/components/RideRequestButton';
import RideRequestErrorDialog from 'ondemand/components/RideRequestErrorDialog';
import ServiceAnnouncementsAlert from 'ondemand/components/ServiceAnnouncementsAlert';
import UserLocation from 'ondemand/components/UserLocation';
import UserLocationMarker from 'ondemand/components/UserLocationMarker';
import VehicleOverlayMarker from 'common/components/VehicleOverlayMarker';
import VehicleIcon from 'ondemand/components/VehicleIcon';

import messages from 'ondemand/messages';

const vehicleColor = '#1E88E5';

const vehicleIsVisible = (rideDetails, vehiclePosition) =>
  vehiclePosition !== null &&
  ((rideDetails.status === rideStatus.PENDING && isNextPickup(rideDetails)) ||
    rideDetails.status === rideStatus.IN_PROGRESS);

class RideMap extends React.Component {
  static displayName = 'RideMap';

  static propTypes = {
    agency: PropTypes.object,
    error: PropTypes.string,
    rideDetails: PropTypes.shape({
      bounds: PropTypes.object,
      pickup: PropTypes.object,
      dropoff: PropTypes.object,
      fare: PropTypes.object,
      origin: PropTypes.object,
      destination: PropTypes.object,
      capacity: PropTypes.number,
      schedule: PropTypes.object,
      status: PropTypes.string,
      wheelchair: PropTypes.bool,
    }).isRequired,
    onErrorConfirm: PropTypes.func.isRequired,
    onRideCancel: PropTypes.func.isRequired,
    service: PropTypes.object,
    serviceLoad: PropTypes.string,
    thresholds: PropTypes.object.isRequired,
    timezone: PropTypes.string.isRequired,
    vehiclePosition: PropTypes.shape({
      driver: PropTypes.object.isRequired,
      heading: PropTypes.number.isRequired,
      position: PropTypes.shape({
        lat: PropTypes.number.isRequired,
        lng: PropTypes.number.isRequired,
      }).isRequired,
    }),
  };

  state = {
    bounds: this.props.rideDetails.bounds,
    center: null,
    userLocation: null,
    isCanceling: false,
    showDriver: false,
  };

  _cancelRide = () => {
    this.setState({isCanceling: false});
    this.props.onRideCancel();
  };

  _handleCancelClick = () => {
    this.setState({isCanceling: true});
  };

  _handleDriverClose = () => {
    this.setState({showDriver: false});
  };

  _handleErrorConfirm = () => {
    this.props.onErrorConfirm();
  };

  _handleMapUserInteraction = () => {
    this.setState({bounds: null, center: null});
  };

  _handleUserLocationChanged = (position) => {
    this.setState({userLocation: position});
  };

  _handleVehicleClick = () => {
    this.setState({showDriver: true});
  };

  _moveToUserLocation = () => {
    this.setState({bounds: null, center: this.state.userLocation});
  };

  render() {
    if (!this.props.rideDetails) return null;

    const {pickup, dropoff, origin, destination, schedule} = this.props.rideDetails;

    const driverModal = !this.state.showDriver ||
      !vehicleIsVisible(this.props.rideDetails, this.props.vehiclePosition)
      ? null
      : <DriverModal
        onClose={this._handleDriverClose}
        vehicle={schedule.vehicle}
      />;

    const userLocationMarker = this.state.userLocation === null
      ? null
      : <UserLocationMarker position={this.state.userLocation} />;

    const vehicleMarker = !vehicleIsVisible(this.props.rideDetails, this.props.vehiclePosition)
      ? null
      : <VehicleOverlayMarker
        heading={this.props.vehiclePosition.heading}
        onClick={this._handleVehicleClick}
        position={this.props.vehiclePosition.position}
        VehicleIcon={VehicleIcon}
        size="medium"
        color={vehicleColor}
      />;

    const controlsNode = (
      <div data-id="ride-overview-container">
        <RideDetailsInput
          agency={this.props.agency}
          load={this.props.serviceLoad}
          ride={this.props.rideDetails}
          thresholds={this.props.thresholds}
          timezone={this.props.timezone}
        />
        {_.contains(
          [rideStatus.NEEDS_APPROVAL, rideStatus.PENDING],
          this.props.rideDetails.status
        ) &&
          <RideRequestButton
            dataId={'cancel-ride-button'}
            color="secondary"
            label="CANCEL RIDE"
            onClick={this._handleCancelClick}
          />
        }
      </div>
    );

    const cancelDialog = (
      <RideCancelModal
        open={this.state.isCanceling}
        onCancel={() => this.setState({isCanceling: false})}
        onConfirm={this._cancelRide}
        needsApproval={this.props.rideDetails.status === rideStatus.NEEDS_APPROVAL}
        paid={this.props.rideDetails.fare && this.props.rideDetails.fare.paid}
      />
    );

    const pendingApprovalDialog = this.props.rideDetails.status !== rideStatus.NEEDS_APPROVAL
      ? null
      : <Confirm
        confirmButtonText="Cancel this request"
        hideCancel
        message={
          <div data-id="ride-pending-approval">
            {messages.ridePendingApproval}
            <div style={{textAlign: 'center', marginTop: 46}}>
              <CircularProgress size={64} thickness={2.5} />
            </div>
          </div>
        }
        onConfirm={() => this.setState({isCanceling: true})}
        open
        title="Ride Pending Approval"
      />;

    const errorDialog = !this.props.error
      ? null
      : <RideRequestErrorDialog onConfirm={this._handleErrorConfirm} message={this.props.error} />;

    return (
      <div data-id={'book-a-ride-container'} style={{height: '100%'}}>
        {errorDialog}
        {pendingApprovalDialog}
        {cancelDialog}
        {driverModal}
        <UserLocation onLocationChanged={this._handleUserLocationChanged} />
        {
          _.contains(
              [rideStatus.NEEDS_APPROVAL, rideStatus.PENDING, rideStatus.IN_PROGRESS],
              this.props.rideDetails.status
          ) ?
          <ServiceAnnouncementsAlert
            announcements={this.props.service.announcements}
            paddingTop="56px"
          /> : null
        }
        <FloatingBarLayout bottom barContent={controlsNode} desktopMax={400} mobileMax={756}>
          <OndemandMap
            bounds={this.state.bounds}
            center={this.state.center}
            onDragend={this._handleMapUserInteraction}
            onZoomChanged={this._handleMapUserInteraction}
          >
            <RideDirectionsMapLayer
              destination={destination}
              dropoff={dropoff}
              origin={origin}
              pickup={pickup}
              originDistance={origin.position ? 1 : 0}
              destinationDistance={destination.position ? 1 : 0}
            />
            {userLocationMarker}
            <Overlay>
              {vehicleMarker}
            </Overlay>
          </OndemandMap>
        </FloatingBarLayout>
      </div>
    );
  }
}

export default RideMap;



// WEBPACK FOOTER //
// ./src/ondemand/components/RideMap.js